// src/components/Home.js
import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import axiosInstance from '../axiosConfig';
import Sidebar from './Sidebar';
import ComponentDetails from './ComponentDetails';
import UpgradeDetails from './UpgradeDetails';
import ReportDetails from './ReportDetails';
import ReportConstructorPage from './ReportConstructorPage';
import InventoryForm from './InventoryForm';
import InventoryDetails from './InventoryDetails';
import Cart from './Cart';
import StorekeeperDashboard from './StorekeeperDashboard';
import PurchaseOrderDetails from './PurchaseOrderDetails';
import PurchaseOrderConstructorPage from './PurchaseOrderConstructorPage';
import OrderHistory from './OrderHistory'; // Новый компонент для истории заказов
import { AuthContext } from '../AuthContext';
import { useNavigate } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { hasGroup } from '../utils/permissions';
import ComponentCloneConstructorPage from './ComponentCloneConstructorPage';
import ComponentModal from './ComponentModal';  // Добавляем компонент
import FileUploadComponent from './FileUploadComponent'; // Импортируем компонент загрузки файла

const isIgnoredRequestError = (error) => (
    error?.code === 'ERR_CANCELED'
    || error?.code === 'ECONNABORTED'
);

function Home() {
    const { selectedServiceCenter, user, otpRequired, logoutUser, setOtpRequired, authTokens } = useContext(AuthContext);
    const navigate = useNavigate();

    const [selectedComponent, setSelectedComponent] = useState(null);
    const [selectedUpgrade, setSelectedUpgrade] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedItems, setExpandedItems] = useState({});
    const [showReport, setShowReport] = useState(false);
    const [showReportConstructor, setShowReportConstructor] = useState(false);
    const [editTemplateId, setEditTemplateId] = useState(null);
    const [loadingData, setLoadingData] = useState(false);
    const [showInventoryForm, setShowInventoryForm] = useState(false);
    const [selectedInventory, setSelectedInventory] = useState(null);
    const [openInventories, setOpenInventories] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const [showDashboard, setShowDashboard] = useState(false);
    const [showPurchaseOrderDetails, setShowPurchaseOrderDetails] = useState(false);
    const [showPurchaseOrderConstructor, setShowPurchaseOrderConstructor] = useState(false);

    const [purchaseOrderId, setPurchaseOrderId] = useState(null);

    const [orders, setOrders] = useState([]);
    const [cartItemsCount, setCartItemsCount] = useState(0);
    const [showComponentCloneConstructor, setShowComponentCloneConstructor] = useState(false);
    const [showComponentModal, setShowComponentModal] = useState(false);
    const [componentCloneUpdated, setComponentCloneUpdated] = useState(false);

    // Новое состояние для отображения FileUploadComponent
    const [showFileUpload, setShowFileUpload] = useState(false);

    // Новое состояние для отображения истории заказов
    const [showOrderHistory, setShowOrderHistory] = useState(false);

    const notificationSoundRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const componentRequestControllerRef = useRef(null);
    const upgradeRequestControllerRef = useRef(null);
    const isStorekeeper = hasGroup(user, 'Кладовщик');

    const [isSidebarHovered, setIsSidebarHovered] = useState(false);
    const [isTooltipHovered, setIsTooltipHovered] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Определяем clearContent первым
    const clearContent = useCallback(() => {
        setSelectedComponent(null);
        setSelectedUpgrade(null);
        setShowReport(false);
        setShowReportConstructor(false);
        setEditTemplateId(null);
        setShowInventoryForm(false);
        setSelectedInventory(null);
        setShowCart(false);
        setShowDashboard(false);
        setShowPurchaseOrderDetails(false);
        setShowPurchaseOrderConstructor(false);
        setShowComponentCloneConstructor(false);  // Очистка формы клонирования компонентов
        setPurchaseOrderId(null);
        setShowFileUpload(false); // Скрываем FileUploadComponent
        setShowOrderHistory(false); // Скрываем историю заказов
    }, []);

    const handleShowOrderHistoryClick = useCallback(() => {
        clearContent();
        setShowOrderHistory(true);
    }, [clearContent]);

    // Определяем handleNotificationClick
    const handleNotificationClick = useCallback(async (orderId) => {
        clearContent();
        setLoadingData(true);

        try {
            const response = await axiosInstance.get('/api/get_pending_orders/');
            setOrders(response.data.orders);
            setShowDashboard(true);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login');
            } else {
                console.error('Error fetching pending orders:', error);
            }
        } finally {
            setLoadingData(false);
        }
    }, [clearContent, logoutUser, navigate]);

    // Определяем showOrderNotification
    const showOrderNotification = useCallback((order) => {
        const orderItems = order.items
            .map(item => `• ${item.component} (x${item.quantity})`)
            .join('\n\n');

        const notificationText = `Новый заказ #${order.order_id} от ${order.user}:\n\n${orderItems}`;

        const toast = window.Toastify({
            text: notificationText,
            duration: -1,
            gravity: "top",
            position: "right",
            backgroundColor: "linear-gradient(to left, #007a6d, #4d8266)",
            stopOnFocus: true,
            className: 'custom-toast',  // Добавляем класс для стилизации
            close: true,  // Включаем кнопку закрытия
            onClick: () => {
                handleNotificationClick(order.order_id);
                toast.hideToast();
            },
        }).showToast();
    }, [handleNotificationClick]);

    // Определяем fetchNotifications
    const fetchNotifications = useCallback(async () => {
        if (!isStorekeeper || !selectedServiceCenter) {
            return;
        }

        try {
            const response = await axiosInstance.get('/api/get_pending_orders/', {
                params: { service_center_id: selectedServiceCenter.id }
            });
            const ordersData = response.data.orders || [];
            setOrders(ordersData);

            const activeNotifications = ordersData.filter(order => !order.is_processed);
            setNotifications(activeNotifications);

            const notifiedOrderIds = JSON.parse(localStorage.getItem('notifiedOrderIds')) || [];

            activeNotifications.forEach(order => {
                if (!notifiedOrderIds.includes(order.order_id)) {
                    showOrderNotification(order);  // Вызываем новую функцию для показа уведомлений
                    notifiedOrderIds.push(order.order_id);

                    if (notificationSoundRef.current) {
                        notificationSoundRef.current.play().catch((error) => {
                            console.error('Error playing notification sound:', error);
                        });
                    }
                }
            });

            localStorage.setItem('notifiedOrderIds', JSON.stringify(notifiedOrderIds));
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login');
            } else {
                console.error('Error fetching notifications:', error);
            }
        }
    }, [isStorekeeper, selectedServiceCenter, logoutUser, navigate, showOrderNotification]);

    // Определяем handleComponentClick
    const handleComponentClick = useCallback(async (componentId) => {
        if (!selectedServiceCenter) {
            navigate('/select-service-center');
            return;
        }

        componentRequestControllerRef.current?.abort();
        const controller = new AbortController();
        componentRequestControllerRef.current = controller;
        setLoadingData(true);
        try {
            const response = await axiosInstance.get(`/api/component/${componentId}/`, {
                params: { service_center: selectedServiceCenter.id },
                signal: controller.signal,
            });

            if (controller.signal.aborted) {
                return;
            }

            clearContent();
            setSelectedComponent(response.data);
            localStorage.setItem('selectedComponent', JSON.stringify(response.data));
            localStorage.removeItem('selectedUpgrade');
        } catch (error) {
            if (isIgnoredRequestError(error)) {
                return;
            }
            if (error.response && error.response.status === 404) {
                console.warn('Component not found, skipping.');
                localStorage.removeItem('selectedComponent');
            } else if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login');
            } else {
                console.error('Error fetching component data:', error);
            }
        } finally {
            if (componentRequestControllerRef.current === controller) {
                componentRequestControllerRef.current = null;
                setLoadingData(false);
            }
        }
    }, [selectedServiceCenter, clearContent, logoutUser, navigate]);

    // Определяем handleUpgradeClick
    const handleUpgradeClick = useCallback(async (upgradeId) => {
        if (!selectedServiceCenter) {
            navigate('/select-service-center');
            return;
        }

        upgradeRequestControllerRef.current?.abort();
        const controller = new AbortController();
        upgradeRequestControllerRef.current = controller;
        setLoadingData(true);
        try {
            const response = await axiosInstance.get(`/api/upgrade/${upgradeId}/`, {
                params: { service_center: selectedServiceCenter.id },
                signal: controller.signal,
            });

            if (controller.signal.aborted) {
                return;
            }

            clearContent();
            setSelectedUpgrade(response.data);
            localStorage.setItem('selectedUpgrade', JSON.stringify(response.data));
            localStorage.removeItem('selectedComponent');
        } catch (error) {
            if (isIgnoredRequestError(error)) {
                return;
            }
            if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login');
            } else {
                console.error('Error fetching upgrade details:', error);
            }
        } finally {
            if (upgradeRequestControllerRef.current === controller) {
                upgradeRequestControllerRef.current = null;
                setLoadingData(false);
            }
        }
    }, [selectedServiceCenter, clearContent, logoutUser, navigate]);

    // Определяем sendOtp
    const sendOtp = useCallback(async () => {
        try {
            await axiosInstance.post('/api/send-otp/', {}, {
                headers: {
                    'Authorization': `Bearer ${authTokens.access}`
                }
            });
            console.log('OTP sent successfully');
        } catch (error) {
            console.error('Error sending OTP:', error);
            if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login'); // Перенаправление на страницу входа
            }
        }
    }, [authTokens.access, logoutUser, navigate]);

    // Определяем resetInactivityTimer
    const resetInactivityTimer = useCallback(() => {
        if (inactivityTimeoutRef.current) {
            clearTimeout(inactivityTimeoutRef.current);
        }
        inactivityTimeoutRef.current = setTimeout(() => {
            setOtpRequired(true);
            sendOtp();
            navigate('/otp-verification');
        }, 1000000);
    }, [setOtpRequired, sendOtp, navigate]);

    // Определяем loadOpenInventories
    const loadOpenInventories = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/update_open_inventories/');
            setOpenInventories(response.data.open_inventories_html || []);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login');
            } else {
                console.error('Error loading open inventories:', error);
            }
        }
    }, [logoutUser, navigate]);

    // Определяем fetchCartItemsCount
    const fetchCartItemsCount = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/cart/');
            setCartItemsCount(response.data.items.length);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login');
            } else {
                console.error('Error fetching cart items count:', error);
            }
        }
    }, [logoutUser, navigate]);

    // Определяем restorePreviousState
    const restorePreviousState = useCallback(async () => {
        const savedComponent = localStorage.getItem('selectedComponent');
        const savedUpgrade = localStorage.getItem('selectedUpgrade');
        const savedExpandedItems = localStorage.getItem('expandedItems');

        if (savedComponent) {
            const component = JSON.parse(savedComponent);
            handleComponentClick(component.id).catch(() => {
                console.warn('Component not found, removing from local storage.');
                localStorage.removeItem('selectedComponent');
            });
        }

        if (savedUpgrade) {
            const upgrade = JSON.parse(savedUpgrade);
            handleUpgradeClick(upgrade.id).catch(() => {
                console.warn('Upgrade not found, removing from local storage.');
                localStorage.removeItem('selectedUpgrade');
            });
        }

        if (savedExpandedItems) {
            setExpandedItems(JSON.parse(savedExpandedItems));
        }
    }, [handleComponentClick, handleUpgradeClick]);

    // Определяем updateProjectsList
    const updateProjectsList = useCallback(() => {
        axiosInstance.get('/api/projects/', {
            headers: {
                'Authorization': `Bearer ${authTokens.access}`,
            },
        })
        .then((response) => {
            // setProjects(response.data.projects || []);
        })
        .catch((error) => {
            console.error('Ошибка при загрузке проектов:', error);
        });
    }, [authTokens.access]);

    // Остальные функции остаются без изменений...

    // Определяем handleComponentModalSubmit
    const handleComponentModalSubmit = useCallback((data) => {
        if (data === "clone") {
            setShowComponentCloneConstructor(true);  // Открываем страницу клонирования
        } else {
            // Обработка других данных, если необходимо
        }
    }, []);

    // Определяем handleShowComponentCloneClick
    const handleShowComponentCloneClick = useCallback(() => {
        clearContent();
        setShowComponentCloneConstructor(true);
    }, [clearContent]);

    // Определяем handleShowReportClick
    const handleShowReportClick = useCallback(() => {
        clearContent();
        setShowReport(true);
    }, [clearContent]);

    // Определяем handleShowReportConstructorClick
    const handleShowReportConstructorClick = useCallback((templateId = null) => {
        clearContent();
        setShowReportConstructor(true);
        setEditTemplateId(templateId);
    }, [clearContent]);

    // Определяем handleExportReport
    const handleExportReport = useCallback(async (startDate, endDate, templateId) => {
        // setDownloading(true);
        try {
            const response = await axiosInstance.get('/api/export_report_to_excel/', {
                params: { start_date: startDate, end_date: endDate, template_id: templateId },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `report_${startDate}_to_${endDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            if (error.response && error.response.status === 401) {
                logoutUser();
                navigate('/login');
            } else {
                console.error('Error exporting report:', error);
            }
        } finally {
            // setDownloading(false);
        }
    }, [logoutUser, navigate]);

    // Определяем toggleExpand
    const toggleExpand = useCallback((id) => {
        setExpandedItems(prevState => ({
            ...prevState,
            [id]: prevState[id] ? null : true
        }));
    }, []);

    // Определяем handleSearchInputChange
    const handleSearchInputChange = useCallback((event) => {
        setSearchQuery(event.target.value);
    }, []);

    // Определяем handleProjectClick
    const handleProjectClick = useCallback((projectId) => {
        clearContent();
        toggleExpand(projectId);
    }, [clearContent, toggleExpand]);

    // Определяем handleCompatibilityClick
    const handleCompatibilityClick = useCallback((compatibilityId) => {
        clearContent();
    }, [clearContent]);

    // Определяем handleShowInventoryForm
    const handleShowInventoryForm = useCallback(() => {
        clearContent();
        setShowInventoryForm(true);
    }, [clearContent]);

    // Определяем handleInventorySelected
    const handleInventorySelected = useCallback((inventoryId) => {
        setSelectedInventory(inventoryId);
        setShowInventoryForm(false);
        loadOpenInventories();
    }, [loadOpenInventories]);

    // Определяем handleCloseInventoryDetails
    const handleCloseInventoryDetails = useCallback(() => {
        setSelectedInventory(null);
        loadOpenInventories();
    }, [loadOpenInventories]);

    // Определяем handleCartClick
    const handleCartClick = useCallback(() => {
        clearContent();
        setShowCart(true);
    }, [clearContent]);

    // Определяем handleOrderIssued
    const handleOrderIssued = useCallback((issuedCartId) => {
        setOrders(prevOrders => prevOrders.filter(order => order.order_id !== issuedCartId));
        setNotifications(prevNotifications => prevNotifications.filter(notification => notification.id !== issuedCartId));
        setCartItemsCount(0);
    }, []);

    // Определяем handleShowPurchaseOrderClick
    const handleShowPurchaseOrderClick = useCallback(() => {
        clearContent();
        setShowPurchaseOrderConstructor(true);
    }, [clearContent]);

    // Определяем handleBackToOrderConstructor
    const handleBackToOrderConstructor = useCallback(() => {
        setShowPurchaseOrderDetails(false);
        setShowPurchaseOrderConstructor(true);
        setPurchaseOrderId(null);
    }, []);

    // Определяем handleShowOrderDetails
    const handleShowOrderDetails = useCallback((orderId) => {
        setPurchaseOrderId(orderId);
        setShowPurchaseOrderConstructor(false);
        setShowPurchaseOrderDetails(true);
    }, []);

    // Определяем handleShowFileUpload
    const handleShowFileUpload = useCallback(() => {
        clearContent();
        setShowFileUpload(true);
    }, [clearContent]);

    // Определяем toggleSidebar
    const toggleSidebar = useCallback(() => {
        setIsSidebarCollapsed(prev => !prev);
    }, []);

    // Определяем handleMouseLeaveSidebar
    const handleMouseLeaveSidebar = useCallback(() => {
        setTimeout(() => {
            if (!isTooltipHovered) {
                setIsSidebarHovered(false);
            }
        }, 300); // Задержка для предотвращения мгновенного скрытия при переходе между элементами
    }, [isTooltipHovered]);

    // Inactivity Timer Effect
    useEffect(() => {
        document.addEventListener('mousemove', resetInactivityTimer);
        document.addEventListener('keypress', resetInactivityTimer);
        resetInactivityTimer();

        return () => {
            clearTimeout(inactivityTimeoutRef.current);
            document.removeEventListener('mousemove', resetInactivityTimer);
            document.removeEventListener('keypress', resetInactivityTimer);
        };
    }, [resetInactivityTimer]);

    // Initial Load Effect
    useEffect(() => {
        if (otpRequired) {
            navigate('/otp-verification');
        } else if (!selectedServiceCenter) {
            navigate('/select-service-center');
        } else {
            loadOpenInventories();
            restorePreviousState();
            fetchCartItemsCount();
        }
    }, [selectedServiceCenter, otpRequired, navigate, loadOpenInventories, restorePreviousState, fetchCartItemsCount]);

    // Update Projects List After Clone
    useEffect(() => {
        if (componentCloneUpdated) {
            updateProjectsList(); // Обновляем список проектов
            setComponentCloneUpdated(false);  // Сбрасываем флаг после обновления
        }
    }, [componentCloneUpdated, updateProjectsList]);

    // Notification Interval Effect
    useEffect(() => {
        if (!isStorekeeper || !selectedServiceCenter?.id || !authTokens?.access) {
            return undefined;
        }

        fetchNotifications();
        const intervalId = setInterval(() => {
            fetchNotifications();
        }, 30000); // Проверка каждые 30 секунд

        return () => clearInterval(intervalId); // Очищаем интервал при размонтировании
    }, [authTokens?.access, fetchNotifications, isStorekeeper, selectedServiceCenter?.id]);

    return (
        <div className="explorer-container">
            <div
                className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${isSidebarHovered ? 'hovered' : ''}`}
                id="sidebar"
                onMouseEnter={() => setIsSidebarHovered(true)}
                onMouseLeave={handleMouseLeaveSidebar}
            >
                <div className="slider" onClick={toggleSidebar}>
                    <i className={`fas ${isSidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
                </div>

                <div className="sidebar-container">
                    <h4>Быстрый доступ</h4>
                    <Sidebar
                        key={selectedServiceCenter ? selectedServiceCenter.id : 'default-sidebar'}
                        onProjectClick={handleProjectClick}
                        onCompatibilityClick={handleCompatibilityClick}
                        onComponentClick={handleComponentClick}
                        onUpgradeClick={handleUpgradeClick}
                        onReportClick={handleShowReportClick}
                        onShowReportConstructor={handleShowReportConstructorClick}
                        onShowInventoryForm={handleShowInventoryForm}
                        onInventorySelected={handleInventorySelected}
                        searchQuery={searchQuery}
                        expandedItems={expandedItems}
                        toggleExpand={toggleExpand}
                        openInventories={openInventories || []}
                        setOpenInventories={setOpenInventories}
                        onShowPurchaseOrder={handleShowPurchaseOrderClick}
                        onShowComponentCloneConstructor={handleShowComponentCloneClick}
                        onShowFileUpload={handleShowFileUpload} // Передаем обработчик для загрузки файла
                        onShowOrderHistory={handleShowOrderHistoryClick} // Добавляем обработчик для истории заказов
                    />
                </div>
            </div>

            <div className={`main-content ${isSidebarCollapsed ? 'expanded' : ''}`}>
                <header className="address-bar d-flex justify-content-between">
                    <div className="search-bar">
                        <input
                            type="text"
                            id="search-input"
                            placeholder="Поиск по складу..."
                            value={searchQuery}
                            onChange={handleSearchInputChange}
                            className="form-control"
                        />
                    </div>
                    <div className="action-buttons d-flex">
                        {!isStorekeeper && (
                            <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="tooltip-cart">Корзина</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setTimeout(() => setIsTooltipHovered(false), 200)}
                            >
                                <button className="btn btn-primary ml-2 position-relative" onClick={handleCartClick}>
                                    <i className="fas fa-shopping-cart"></i>
                                    {cartItemsCount > 0 && (
                                        <span className="badge badge-light position-absolute" style={{ top: '-5px', right: '-10px' }}>
                                            {cartItemsCount}
                                        </span>
                                    )}
                                </button>
                            </OverlayTrigger>
                        )}
                        {isStorekeeper && (
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip id="tooltip-notifications">Уведомления</Tooltip>}
                                onMouseEnter={() => setIsTooltipHovered(true)}
                                onMouseLeave={() => setTimeout(() => setIsTooltipHovered(false), 200)}
                            >
                                <button className="btn btn-warning ml-2 position-relative" onClick={handleNotificationClick}>
                                    <i className="fas fa-bell"></i>
                                    {notifications.length > 0 && (
                                        <span className="badge badge-light position-absolute" style={{ top: '-5px', right: '-10px' }}>
                                            {notifications.length}
                                        </span>
                                    )}
                                </button>
                            </OverlayTrigger>
                        )}
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="tooltip-history">История заказов</Tooltip>} // Иконка для истории заказов
                        >
                            <button className="btn btn-info ml-2" onClick={handleShowOrderHistoryClick}>
                                <i className="fas fa-history"></i>
                            </button>
                        </OverlayTrigger>
                        <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip id="tooltip-logout">Выйти</Tooltip>}
                            onMouseEnter={() => setIsTooltipHovered(true)}
                            onMouseLeave={() => setTimeout(() => setIsTooltipHovered(false), 200)}
                        >
                            <button className="btn btn-danger ml-2" onClick={logoutUser}>
                                <i className="fas fa-sign-out-alt"></i>
                            </button>
                        </OverlayTrigger>
                    </div>
                </header>
                <div id="content-area" className="flex-grow-1 d-flex">
                    {loadingData ? (
                        <div className="loading-spinner-container">
                            <div className="loading-spinner">Загрузка...</div>
                        </div>
                    ) : (
                        <>
                            {showInventoryForm && (
                                <InventoryForm
                                    onClose={() => {
                                        setShowInventoryForm(false);
                                        loadOpenInventories();
                                    }}
                                    onInventoryCreated={handleInventorySelected}
                                    loadOpenInventories={loadOpenInventories}
                                />
                            )}
                            {selectedInventory && (
                                <InventoryDetails
                                    inventoryId={selectedInventory}
                                    onClose={handleCloseInventoryDetails}
                                    loadOpenInventories={loadOpenInventories}
                                />
                            )}
                            {showReport && (
                                <ReportDetails
                                    handleExportReport={handleExportReport}
                                    onCreateTemplate={() => handleShowReportConstructorClick()}
                                    onEditTemplate={(templateId) => handleShowReportConstructorClick(templateId)}
                                />
                            )}
                            {showReportConstructor && (
                                <ReportConstructorPage
                                    templateId={editTemplateId}
                                    onSaveSuccess={handleShowReportClick}
                                />
                            )}
                            {selectedComponent && <ComponentDetails component={selectedComponent} onUpdateCart={fetchCartItemsCount} />}
                            {selectedUpgrade && <UpgradeDetails upgrade={selectedUpgrade} onUpdateCart={fetchCartItemsCount} />}
                            {showCart && <Cart onOrderComplete={fetchCartItemsCount} updateCartItemsCount={fetchCartItemsCount} />}
                            {showDashboard && <StorekeeperDashboard orders={orders} onOrderIssued={handleOrderIssued} />}
                            {showPurchaseOrderConstructor && (
                                <PurchaseOrderConstructorPage
                                    onSaveSuccess={handleShowOrderDetails}
                                    onSelectOrder={handleShowOrderDetails}
                                />
                            )}
                            {showPurchaseOrderDetails && (
                                <PurchaseOrderDetails
                                    orderId={purchaseOrderId}
                                    onClose={handleBackToOrderConstructor}
                                />
                            )}
                            {showComponentCloneConstructor && (
                                <ComponentCloneConstructorPage
                                    onSaveSuccess={() => {
                                        setComponentCloneUpdated(true);  // Устанавливаем флаг для обновления списка проектов
                                        setShowComponentCloneConstructor(false);
                                        window.Toastify({
                                            text: "Компоненты успешно созданы на основе существующих.",
                                            duration: 3000,
                                            gravity: "top",
                                            position: "right",
                                            backgroundColor: "#00b09b"
                                        }).showToast();
                                    }}
                                />
                            )}
                            {showFileUpload && (
                                <FileUploadComponent />
                            )}
                            {showOrderHistory && <OrderHistory />} {/* Добавляем компонент для отображения истории */}
                        </>
                    )}
                </div>
            </div>
            <audio ref={notificationSoundRef} src="/sounds/notification.wav" preload="auto" />
            {/* Модальное окно для добавления компонента */}
            <ComponentModal 
                show={showComponentModal} 
                handleClose={() => setShowComponentModal(false)} 
                onSubmit={handleComponentModalSubmit} 
            />
        </div>
    );

}

export default Home;
